import * as React from 'react';
import { AdvancedMarker, InfoWindow } from '@vis.gl/react-google-maps';
import { ConsumerPurchaseSummary } from 'generated/graphql';
import { core } from '@tock/coretypes';
import { mapPinIcon } from 'utils/mapsUtils';
import PopUp from 'components/profile/culinary-travel-log/PopUp';

// Width of the info window content. Should match .TravelLogMap-infoBoxContainer width.
const INFOBOX_WIDTH_PX = 231;

interface Props {
  id: string;
  location: google.maps.LatLngLiteral;
  business: core.IBusinessPublic;
  purchases: ConsumerPurchaseSummary[];
  isOpen: boolean;

  onOpen(id: string): void;
  onClose(id: string): void;
}

/**
 * vis.gl (flag-ON) travel-log marker. Renders the `mapPinIcon` SVG as
 * `<AdvancedMarker>` content (default bottom-center anchor, matching the legacy
 * `google.maps.Marker` pin) and replaces the legacy `<InfoBox>` popup with
 * `<InfoWindow>` (vis.gl has no `InfoBox` equivalent). The a11y affordances
 * (keyboard toggle, focus open/close, role/aria) are preserved on the marker
 * content wrapper.
 */
const RestaurantMarkerNew: React.FunctionComponent<Props> = ({
  id,
  location,
  business,
  purchases,
  isOpen,
  onOpen,
  onClose,
}) => {
  const pinSize = isOpen ? 35 : 24;
  const icon = mapPinIcon(pinSize);

  const handleToggle = () => {
    if (isOpen) {
      onClose(id);
    } else {
      onOpen(id);
    }
  };

  return (
    <>
      <AdvancedMarker position={location} onClick={handleToggle}>
        <div
          onKeyPress={(event) => {
            if (event.key === 'Enter') {
              handleToggle();
            }
          }}
          onFocus={() => onOpen(id)}
          onBlur={() => onClose(id)}
          tabIndex={0}
          role="button"
          aria-label={business.name || undefined}
          aria-expanded={isOpen}
        >
          <img src={icon.url} width={pinSize} height={pinSize} alt="" />
        </div>
      </AdvancedMarker>
      {isOpen && (
        <InfoWindow
          position={location}
          minWidth={INFOBOX_WIDTH_PX}
          onCloseClick={() => onClose(id)}
        >
          <PopUp business={business} purchases={purchases} />
        </InfoWindow>
      )}
    </>
  );
};

export default RestaurantMarkerNew;
